import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { normalizeUnit, familyUnits } from '../utils/units';
import './OptionConsumptionEditor.css';

/**
 * Consumo de inventario de una opción de modificador (B3 / RES-31).
 *
 * "Extra queso" descuenta 30 g más de queso además de la receta del producto; "sin cebolla" no
 * declara nada y no genera movimiento. Misma UI que los ingredientes de la receta del producto:
 * insumo del catálogo + cantidad + unidad de la misma familia.
 *
 * @param {Object} props - Props
 * @param {Array<{inventoryItemId: string, quantity: number, unit: string}>} [props.consumption] - Líneas actuales
 * @param {Array<Object>} [props.inventoryItems] - Catálogo de insumos
 * @param {Function} props.onChange - Recibe las líneas nuevas
 * @param {string} props.optionLabel - Nombre de la opción, para las etiquetas accesibles
 * @returns {JSX.Element} Editor
 */
function OptionConsumptionEditor({ consumption, inventoryItems = [], onChange, optionLabel }) {
  const { t } = useTranslation();
  const lines = Array.isArray(consumption) ? consumption : [];

  const [selectedId, setSelectedId] = useState('');
  const [quantity, setQuantity] = useState('');
  const [unit, setUnit] = useState('');
  const [open, setOpen] = useState(lines.length > 0);

  const handleSelect = (e) => {
    const id = e.target.value;
    setSelectedId(id);
    const item = inventoryItems.find((i) => i.itemId === id);
    // Propone la unidad del insumo; el selector deja elegir otras de la misma familia (RES-17)
    setUnit(item ? normalizeUnit(item.unit) || 'un' : '');
  };

  // `'0'` es truthy en JS: con `!quantity` el botón quedaba habilitado y se agregaba una línea
  // con cantidad 0 que el backend rechaza con 400, tirando abajo el PUT del grupo entero y
  // perdiendo toda la edición, no solo esa línea (RES-60).
  const parsedQuantity = parseFloat(quantity);
  const quantityIsValid = Number.isFinite(parsedQuantity) && parsedQuantity > 0;

  const addLine = () => {
    const item = inventoryItems.find((i) => i.itemId === selectedId);
    if (!item || !quantityIsValid) return;
    onChange([
      ...lines,
      {
        inventoryItemId: selectedId,
        quantity: parsedQuantity,
        unit: unit || normalizeUnit(item.unit) || 'un',
        name: item.name,
      },
    ]);
    setSelectedId('');
    setQuantity('');
    setUnit('');
  };

  const removeLine = (index) => {
    onChange(lines.filter((_, i) => i !== index));
  };

  const nameOf = (line) =>
    line.name || inventoryItems.find((i) => i.itemId === line.inventoryItemId)?.name || '—';

  return (
    <div className="option-consumption">
      <button
        type="button"
        className="option-consumption-toggle"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        {t('modifiers.consumption.title')}
        {lines.length > 0 && <span className="option-consumption-count">{lines.length}</span>}
      </button>

      {open && (
        <div className="option-consumption-body">
          <p className="text-muted small">{t('modifiers.consumption.help')}</p>

          {lines.length > 0 && (
            <ul className="list-group option-consumption-list">
              {lines.map((line, index) => (
                <li key={index} className="list-group-item">
                  <span>
                    {nameOf(line)} — {line.quantity} {line.unit}
                  </span>
                  <button
                    type="button"
                    className="btn btn-sm btn-danger"
                    onClick={() => removeLine(index)}
                    aria-label={t('modifiers.consumption.remove')}
                  >
                    x
                  </button>
                </li>
              ))}
            </ul>
          )}

          <div className="recipe-form-row">
            <div className="recipe-form-field flex-2">
              <label>
                {t('modifiers.consumption.itemLabel')}
                <select
                  value={selectedId}
                  onChange={handleSelect}
                  aria-label={`${t('modifiers.consumption.itemLabel')} — ${optionLabel}`}
                >
                  <option value="">{t('modifiers.consumption.selectItem')}</option>
                  {inventoryItems.map((item) => (
                    <option key={item.itemId} value={item.itemId}>
                      {item.name} ({normalizeUnit(item.unit) || item.unit})
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <div className="recipe-form-field flex-1">
              <label>
                {t('modifiers.consumption.quantity')}
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  aria-invalid={quantity !== '' && !quantityIsValid}
                  aria-label={`${t('modifiers.consumption.quantity')} — ${optionLabel}`}
                />
              </label>
              {quantity !== '' && !quantityIsValid && (
                <p className="option-consumption-hint">
                  {t('modifiers.consumption.quantityInvalid')}
                </p>
              )}
            </div>
            <div className="recipe-form-field flex-1">
              <label>
                {t('modifiers.consumption.unit')}
                <select
                  value={unit}
                  onChange={(e) => setUnit(e.target.value)}
                  disabled={!selectedId}
                  aria-label={`${t('modifiers.consumption.unit')} — ${optionLabel}`}
                >
                  {(unit ? familyUnits(unit) : ['']).map((u) => (
                    <option key={u} value={u}>
                      {u}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <button
              type="button"
              onClick={addLine}
              className="btn btn-sm btn-secondary"
              disabled={!selectedId || !quantityIsValid}
              aria-label={t('modifiers.consumption.add')}
            >
              +
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default OptionConsumptionEditor;
